import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ProductFiltersFormValues } from "../filters/ProductFilterFields.js";
import { useProductTypeInput_PimSearchProductTypesQuery } from "../ProductTypeInput.generated.js";
import { ProductSearchModal } from "./ProductSearchModal.js";

interface Props {
  productTypeId: string;
  handleClose: () => void;
}

/**
 * The product search opened to set preferences for one product type, not to pick from.
 *
 * The product type is the only filter carried in, and it stays removable: the preference is
 * keyed to the product type the caller pinned, so widening the search only widens the
 * candidates — an article the catalogue has not classified yet can be preferred here.
 */
export function ProductPreferencesSearchModal({
  productTypeId,
  handleClose,
}: Props) {
  const { t } = useTranslate(["Product"]);
  const client = useApolloClient();

  const query = useProductTypeInput_PimSearchProductTypesQuery({
    client,
    variables: { productTypeIds: [productTypeId] },
  });
  const productTypeLabel =
    getDataOrNull(query.data?.pimSearchProductTypes)?.productTypes?.[0]
      ?.label ?? null;

  const initialFilters = React.useMemo(
    (): Partial<ProductFiltersFormValues> => ({
      productTypeIds: [productTypeId],
    }),
    [productTypeId]
  );

  return (
    <ProductSearchModal
      title={
        productTypeLabel
          ? t("Preferences for {productType}", {
              ns: "Product",
              productType: productTypeLabel,
            })
          : t("Product preferences", { ns: "Product" })
      }
      initialFilters={initialFilters}
      pinnedPreferenceProductTypeId={productTypeId}
      canChangeProductType
      handleClose={handleClose}
    />
  );
}
